import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  BUDGETS,
  DELIVERY_TIMES,
  PROJECT_TYPES,
  type EnumsResponse,
} from '@/types';
import { useProjectCommand } from '@/hooks/useProjectCommand';
import { clearProjectCommand } from '@/services/projectCommands';
import { putProjectBrief } from '@/api/adapter/projectBrief';
import {
  loadSubmissionDraft,
  saveSubmissionDraft,
  submissionDraftKey,
  projectBriefFromForm,
  validDeliveryDate,
} from '@/services/projectSubmission';
import { useAuthStore } from '@/stores/authStore';
import { useDaoConfig } from '@/hooks/useDaoConfig';
import './components/CreateProject/submission.css';
import { MainScreen } from '@components/shared/MainScreen/MainScreen';

import {
  createProjectSchema,
  FormData,
  STEP1_FIELDS,
} from './components/CreateProject/schema';
import { Step1 } from './components/CreateProject/Step1';
import { Step2 } from './components/CreateProject/Step2';
import { Step3 } from './components/CreateProject/Step3';
import { StepperNumeric } from './components/CreateProject/StepperNumeric';

export default function CreateProjectPage() {
  const principal = useAuthStore((state) => state.session?.principalId);
  return principal ? (
    <ProjectSubmission key={principal} principal={principal} />
  ) : null;
}
function ProjectSubmission({ principal }: { principal: string }) {
  const navigate = useNavigate();
  const command = useProjectCommand();
  const { projectTypes } = useDaoConfig();
  const enums: EnumsResponse = React.useMemo(
    () => ({
      budgets: BUDGETS,
      deliveryTimes: DELIVERY_TIMES,
      projectTypes: projectTypes.length > 0 ? projectTypes : PROJECT_TYPES,
      skills: [],
      roles: [],
      availability: [],
      languages: {},
      proficiency: [],
    }),
    [projectTypes]
  );
  const [saved] = React.useState(() => {
    try {
      return { draft: loadSubmissionDraft(principal), error: '' };
    } catch (reason) {
      return {
        draft: null,
        error:
          reason instanceof Error
            ? reason.message
            : 'Cannot read the saved draft.',
      };
    }
  });
  const [createdProjectId, setCreatedProjectId] = React.useState(
    saved.draft?.createdProjectId ?? null
  );
  const [error, setError] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const active = React.useRef(true);
  const briefRequest = React.useRef<AbortController>();
  React.useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
      briefRequest.current?.abort();
    };
  }, []);
  const locked =
    command.busy ||
    command.pending ||
    command.blocked ||
    !!createdProjectId ||
    submitting ||
    !!saved.error;
  const user = useAuthStore((s) => s.user);

  const [currentStep, setCurrentStep] = React.useState(
    saved.draft?.createdProjectId || command.saved ? 3 : 1
  );
  const [objectives, setObjectives] = React.useState<string[]>(
    saved.draft?.objectives ?? ['']
  );
  const [constraints, setConstraints] = React.useState<string[]>(
    saved.draft?.constraints ?? []
  );

  const {
    register,
    control,
    handleSubmit,
    watch,
    trigger,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(createProjectSchema),
    defaultValues: saved.draft?.values ?? {
      title: '',
      summary: '',
      description: '',
      url: '',
      projectType: -1,
      budget: 0,
      deliveryTime: 3,
      deliveryDate: '',
    },
  });

  React.useEffect(() => {
    if (saved.error) return;
    const subscription = watch((values) => {
      try {
        saveSubmissionDraft(principal, {
          values: values as FormData,
          objectives,
          constraints,
          createdProjectId,
        });
      } catch {
        setError(
          'Cannot save the project draft in this browser. Keep this page open and enable session storage before submitting.'
        );
      }
    });
    return () => subscription.unsubscribe();
  }, [
    watch,
    principal,
    objectives,
    constraints,
    createdProjectId,
    saved.error,
  ]);
  React.useEffect(() => {
    if (saved.error) return;
    try {
      saveSubmissionDraft(principal, {
        values: getValues(),
        objectives,
        constraints,
        createdProjectId,
      });
    } catch {
      setError(
        'Cannot save the project draft in this browser. Keep this page open and enable session storage before submitting.'
      );
    }
  }, [
    getValues,
    principal,
    objectives,
    constraints,
    createdProjectId,
    saved.error,
  ]);
  const summaryValue = watch('summary');
  const summaryLength = [...(summaryValue ?? '')].length;
  const deliveryTimeValue = Number(watch('deliveryTime'));

  // ---- Step navigation ----

  async function goToStep2() {
    const valid = await trigger(STEP1_FIELDS);
    if (valid) setCurrentStep(2);
  }

  async function goToStep3() {
    const fields: (keyof FormData)[] = ['budget', 'deliveryTime'];
    if (deliveryTimeValue === 3) fields.push('deliveryDate');
    const valid = await trigger(fields);
    if (valid) {
      try {
        if (deliveryTimeValue === 3)
          validDeliveryDate(getValues('deliveryDate'));
        projectBriefFromForm(getValues(), objectives, constraints);
        setError('');
        setCurrentStep(3);
      } catch (reason) {
        setError(
          reason instanceof Error
            ? reason.message
            : 'Check your objectives and delivery preferences.'
        );
      }
    }
  }

  // ---- Objectives ----

  function addObjective() {
    setObjectives((prev) => [...prev, '']);
  }
  function updateObjective(index: number, value: string) {
    setObjectives((prev) => prev.map((o, i) => (i === index ? value : o)));
  }
  function removeObjective(index: number) {
    setObjectives((prev) => prev.filter((_, i) => i !== index));
  }
  function moveObjective(index: number, dir: 'up' | 'down') {
    setObjectives((prev) => {
      const arr = [...prev];
      const t = dir === 'up' ? index - 1 : index + 1;
      if (t < 0 || t >= arr.length) return prev;
      const tmp = arr[index]!;
      arr[index] = arr[t]!;
      arr[t] = tmp;
      return arr;
    });
  }

  // ---- Constraints ----

  function addConstraint() {
    setConstraints((prev) => [...prev, '']);
  }
  function updateConstraint(index: number, value: string) {
    setConstraints((prev) => prev.map((c, i) => (i === index ? value : c)));
  }
  function removeConstraint(index: number) {
    setConstraints((prev) => prev.filter((_, i) => i !== index));
  }

  // ---- Submit (step 3) ----

  const onSubmit = async (data: FormData) => {
    if (submitting || saved.error || command.blocked) return;
    setSubmitting(true);
    setError('');
    try {
      const brief = projectBriefFromForm(data, objectives, constraints);
      saveSubmissionDraft(principal, {
        values: data,
        objectives,
        constraints,
        createdProjectId,
      });
      let id = createdProjectId;
      if (!id) {
        const result = await command.run(
          command.pending
            ? null
            : {
                kind: 'create',
                payload: { title: data.title, description: data.description },
              }
        );
        if (!result) return;
        id = result.receipt?.createdEntityId ?? null;
        if (!id)
          throw new Error('The creation receipt did not contain a project ID.');
        if (
          !active.current ||
          useAuthStore.getState().session?.principalId !== principal
        )
          return;
        setCreatedProjectId(id);
        saveSubmissionDraft(principal, {
          values: data,
          objectives,
          constraints,
          createdProjectId: id,
        });
      }
      if (
        !active.current ||
        useAuthStore.getState().session?.principalId !== principal
      )
        return;
      briefRequest.current = new AbortController();
      await putProjectBrief(id, brief, briefRequest.current.signal);
      if (
        !active.current ||
        useAuthStore.getState().session?.principalId !== principal
      )
        return;
      clearProjectCommand(principal);
      sessionStorage.removeItem(submissionDraftKey(principal));
      navigate(`/projects/${id}`);
    } catch (reason) {
      if (!active.current) return;
      setError(
        reason instanceof Error
          ? reason.message
          : 'Cannot save the project. Retry this submission.'
      );
    } finally {
      if (active.current) setSubmitting(false);
    }
  };

  // ---- Render Content ----
  const renderContent = () => {
    return (
      <div className="projectSubmissionGrid">
        {/* Stepper */}
        <div className="hidden flex-shrink-0 pt-[21px] lg:block">
          <StepperNumeric currentStep={currentStep} />
        </div>

        {/* Content */}
        <div className="min-w-0 text-left">
          {(saved.error || error || command.error) && (
            <p
              role="alert"
              className="mb-6 rounded-[12px] border border-red-500/30 bg-red-500/10 p-4 text-red-400"
            >
              {saved.error || error || command.error}
            </p>
          )}
          {command.status && (
            <p role="status" className="mb-4 text-[--text-dark-secondary]">
              {command.status}
            </p>
          )}
          {command.pending && !createdProjectId && (
            <p className="mb-4 text-[--text-dark-secondary]">
              A creation request is pending. Submit again to check its result
              without creating another project.
            </p>
          )}
          {createdProjectId && (
            <p className="mb-4 text-[--text-dark-secondary]">
              Your project was created. Retry submission to save its remaining
              details.
            </p>
          )}
          <fieldset disabled={locked && currentStep !== 3}>
            {currentStep === 1 && (
              <Step1
                register={register}
                control={control}
                errors={errors}
                summaryLength={summaryLength}
                enums={enums}
                onNext={goToStep2}
              />
            )}

            {currentStep === 2 && (
              <Step2
                control={control}
                register={register}
                errors={errors}
                enums={enums}
                deliveryTimeValue={deliveryTimeValue}
                objectives={objectives}
                constraints={constraints}
                onAddObjective={addObjective}
                onUpdateObjective={updateObjective}
                onRemoveObjective={removeObjective}
                onMoveObjective={moveObjective}
                onAddConstraint={addConstraint}
                onUpdateConstraint={updateConstraint}
                onRemoveConstraint={removeConstraint}
                onBack={() => setCurrentStep(1)}
                onNext={goToStep3}
              />
            )}

            {currentStep === 3 && (
              <Step3
                values={getValues()}
                objectives={objectives}
                constraints={constraints}
                enums={enums}
                userName={user?.name ?? 'Unknown'}
                isSubmitting={
                  isSubmitting ||
                  submitting ||
                  command.busy ||
                  !!saved.error ||
                  command.blocked
                }
                readOnly={locked}
                onBack={() => setCurrentStep(2)}
                onSubmit={handleSubmit(onSubmit)}
                onEdit={() => setCurrentStep(1)}
              />
            )}
          </fieldset>
        </div>
      </div>
    );
  };

  return (
    <div className="projectSubmission">
      <MainScreen
        backRoute="/dashboard"
        contentClassName="px-4 py-8 sm:px-8 lg:px-14 2xl:px-[112px] 2xl:pt-[52px]"
      >
        {renderContent()}
      </MainScreen>
    </div>
  );
}
